import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, UserCheck, Shield } from "lucide-react";

const ROLES = [
  { value: "system_admin", label: "System Admin", desc: "Full access to all features" },
  { value: "group_fc", label: "Group FC", desc: "All entities, categories, branding" },
  { value: "company_cfo", label: "Company CFO", desc: "Own entity full access" },
  { value: "asset_manager", label: "Asset Manager", desc: "Assets, utilisation, count" },
  { value: "accountant", label: "Accountant", desc: "Read-only, download journals" },
  { value: "capex_manager", label: "Capex Manager", desc: "WIP and milestones" },
  { value: "data_entry", label: "Data Entry Clerk", desc: "Utilisation, revenue only" },
  { value: "auditor", label: "Auditor", desc: "Read-only across all entities" },
];

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "asset_manager", password: "" });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/users");
        setUsers(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const createUser = async () => {
    try {
      await api.post("/users", form);
      setShowForm(false);
      setForm({ email: "", name: "", role: "asset_manager", password: "" });
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { alert(e.response?.data?.detail || "Error creating user"); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">User Management</h1>
          <p className="text-sm text-[#6B7280] mt-1">{users.length} users in your tenant</p>
        </div>
        <button onClick={() => setShowForm(true)} className="stratum-btn-primary flex items-center gap-2" data-testid="add-user-btn">
          <Plus size={16} /> Invite User
        </button>
      </div>

      {showForm && (
        <div className="stratum-card p-6 max-w-lg">
          <h3 className="font-display text-lg font-semibold mb-4">Invite New User</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Name</label>
              <input className="stratum-input w-full" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="user-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Email</label>
              <input type="email" className="stratum-input w-full" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="user-email-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Temporary Password</label>
              <input className="stratum-input w-full" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder="Welcome2026!" data-testid="user-password-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Role</label>
              <select className="stratum-input w-full" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} data-testid="user-role-select">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} - {r.desc}</option>)}
              </select>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm">Cancel</button>
              <button onClick={createUser} className="stratum-btn-primary" data-testid="create-user-btn">Create User</button>
            </div>
          </div>
        </div>
      )}

      <div className="stratum-card overflow-hidden">
        <table className="w-full stratum-table" data-testid="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-[#6B7280]">Loading...</td></tr>
            ) : users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td className="font-medium text-sm">{u.name}</td>
                <td className="text-sm text-[#6B7280]">{u.email}</td>
                <td>
                  <span className="flex items-center gap-1.5">
                    <Shield size={12} className="text-[#C8A961]" />
                    <span className="text-xs font-medium capitalize">{u.role?.replace("_", " ")}</span>
                  </span>
                </td>
                <td>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? "bg-[#065F46]/10 text-[#065F46]" : "bg-[#991B1B]/10 text-[#991B1B]"}`}>
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="text-xs text-[#6B7280] font-mono tabular-nums">{u.created_at?.substring(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* RBAC Reference */}
      <div className="stratum-card p-6">
        <h3 className="font-display text-lg font-semibold mb-4">Role Permissions Reference</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#E8E2D5]">
                <th className="text-left py-2 px-3">Role</th>
                <th className="text-left py-2 px-3">Read</th>
                <th className="text-left py-2 px-3">Edit / Create</th>
                <th className="text-left py-2 px-3">Approve / Post</th>
              </tr>
            </thead>
            <tbody className="text-[#6B7280]">
              {ROLES.map(r => (
                <tr key={r.value} className="border-b border-[#E8E2D5]">
                  <td className="py-2 px-3 font-medium text-[#1A1A1A]">{r.label}</td>
                  <td className="py-2 px-3">{r.value === "auditor" ? "All entities" : r.value.includes("admin") || r.value === "group_fc" ? "All" : "Own entity"}</td>
                  <td className="py-2 px-3">{r.desc}</td>
                  <td className="py-2 px-3">{["system_admin", "group_fc", "company_cfo"].includes(r.value) ? "Yes" : "Limited"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
